import {Injectable}                      from '@angular/core';
import {Store}                           from '../index';
import {ApSignalrService}                from '../../ap-core/services/ap-signalr.service';
import {SafeBehaviorSubject}             from 'ts-tooling';
import {IStateStore}                     from '../../ap-interface';
import IFarmCropV2Cr = Data.FieldManagement.IFarmCropV2Cr;
import {Observable, tap} from 'rxjs';
import DatabaseNotifyOperation = Agriport.Invoker.Api.Database.DatabaseNotifyOperation;
import {nameOf}                          from '../../ap-core/utils/ap-name-of';
import {ApMapInstance}                                                                                    from '../../ap-map';
import {
  FarmCropV2CrActiveLoad,
  FarmCropV2CrActiveLoadSuccess,
  FarmCropV2CrLoad,
  FarmCropV2CrLoadSuccess
} from '@backend';

interface IFarmCropV2CrStore extends IStateStore<IFarmCropV2Cr> {
  activeFarmCrops: IFarmCropV2Cr[];
}

/**
 * Represents client store for new V2Cr farm.farm_crop table
 */
@Injectable({providedIn: 'root'})
export class FarmCropV2CrStore extends Store<IFarmCropV2CrStore> {
  constructor(public backend: ApSignalrService) {
    super(backend, {
      loaded: false,
      loading: false,
      data: [],
      activeFarmCrops: []
    });
    backend.registerObservable(FarmCropV2CrActiveLoadSuccess).subscribe(d => {
      super.Mutate(s => s.activeFarmCrops, () => d.Data);
      super.SetLoadFinishState();
    });
    backend.registerObservable(FarmCropV2CrLoadSuccess).subscribe(d => {
      super.Mutate(s => s.data, () => d.Data);
      super.SetLoadFinishState();
    });
    // V2CR For initial debugging and testing
    super.Listen(s => s.activeFarmCrops).subscribe(farmCrops => {
      if (!ApMapInstance.hasDeveloperRole) {
        return;
      }
      const simplifiedFarmCrops = farmCrops.map(farmCrop => ({
        Id: farmCrop.Id,
        Name: farmCrop.TranslationKey,
        IsActive: farmCrop.IsActive,
        YieldExpected: farmCrop.YieldExpected
      }));

      ApMapInstance.printDebugData('🌾 Active farm_crops', simplifiedFarmCrops);
    });
    // V2CR For initial debugging and testing
    super.Listen(s => s.data).subscribe(farmCrops => {
      if (!ApMapInstance.hasDeveloperRole) {
        return;
      }
      const simplifiedFarmCrops = farmCrops.map(farmCrop => ({
        Id: farmCrop.Id,
        Name: farmCrop.TranslationKey,
        IsActive: farmCrop.IsActive,
        YieldExpected: farmCrop.YieldExpected
      }));

      ApMapInstance.printDebugData('🌾 Inactive farm_crops', simplifiedFarmCrops);
    });
  }

  public get Loading$(): Observable<boolean> {
    return super.Listen(s => s.loading);
  }

  public get FarmCrops$(): SafeBehaviorSubject<IFarmCropV2Cr[]> {
    return super.Listen(s => s.data);
  }

  public get FarmCrops(): IFarmCropV2Cr[] {
    return this.FarmCrops$.getValue();
  }

  public get ActiveFarmCrops$(): SafeBehaviorSubject<IFarmCropV2Cr[]> {
    return super.Listen(s => s.activeFarmCrops);
  }

  public get ActiveFarmCrops(): IFarmCropV2Cr[] {
    return this.ActiveFarmCrops$.getValue();
  }

  public loadFarmCrops(): void {
    super.SetLoadState();
    super.Mutate(s => s.data, () => []);
    this.DispatchBackend(new FarmCropV2CrLoad([]));
  }

  public loadActiveFarmCrops(): void {
    super.SetLoadState();
    super.Mutate(s => s.activeFarmCrops, () => []);
    this.DispatchBackend(new FarmCropV2CrActiveLoad([]));
  }

  public UpdateSource(operation: DatabaseNotifyOperation, item: IFarmCropV2Cr): void {
    const activeFarmCrops = this.Listen(s => s.activeFarmCrops).getValue() ?? [];
    const inactiveFarmCrops = this.Listen(s => s.data).getValue() ?? [];
    const activeFarmCropPosition = activeFarmCrops.findIndex(i => !!i && !!item && i.Id === item.Id);
    const inactiveFarmCropPosition = inactiveFarmCrops.findIndex(i => !!i && !!item && i.Id === item.Id);
    // const itemExistsInList = position >= 0;
    const activeFarmCropModel = nameOf<IFarmCropV2CrStore>('activeFarmCrops');
    const inactiveFarmCropModel = nameOf<IFarmCropV2CrStore>('data');

    // Add/Update active items
    if ((operation === DatabaseNotifyOperation.Insert || operation === DatabaseNotifyOperation.Update) && item.IsActive) {
      super.UpsertData(activeFarmCropPosition, item, activeFarmCropModel);
      super.RemoveData(inactiveFarmCropPosition, inactiveFarmCropModel);
    }
    // drop inactive or deleted items
    else if ((operation === DatabaseNotifyOperation.Delete || !item.IsActive)) {
      super.RemoveData(activeFarmCropPosition, activeFarmCropModel);
      super.UpsertData(inactiveFarmCropPosition, item, inactiveFarmCropModel);
    }
  }
}
